import { useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getJournalEntries, voidJournalEntry } from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import SelectBox from "../components/UI/SelectBox";
import FloatingInput from "../components/UI/FloatingInput";
import DataTable from "../components/UI/DataTable";
import PageHeader from "../components/UI/PageHeader";
import ConfirmModal from "../components/UI/ConfirmModal";
import { notify } from "../utils/notify";
import { I18nContext, useTranslation } from "../context/I18nContext";

function money(n) {
  return Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const SOURCE_TYPES = ["manual", "sale", "return", "shift_close", "expense"];

export default function JournalEntries() {
  // Each line's Account name can come back translated per the signed-in
  // user's own language (see ResolveEntityNames in
  // entity_translation_handler.go and journal_entry_handler.go) — so this
  // list needs to re-fetch whenever languageCode changes, not just on
  // mount — same reasoning as Products.js.
  const { languageCode } = useContext(I18nContext);
  const { t } = useTranslation("accounting");
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0, serverLimit: 10 });
  const [modalConfig, setModalConfig] = useState({ isOpen: false, id: null });
  const [status, setStatus] = useState("idle");
  const [detailEntry, setDetailEntry] = useState(null);

  const currentPage = parseInt(searchParams.get("page") || "1");
  const dateFrom = searchParams.get("date_from") || "";
  const dateTo = searchParams.get("date_to") || "";
  const sourceType = searchParams.get("source_type") || "";

  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const SOURCE_OPTIONS = [
    { value: "", label: t("filter_source_all_option", "All sources") },
    ...SOURCE_TYPES.map((s) => ({ value: s, label: t(`source_type_${s}`, s) })),
  ];

  const fetchEntries = async () => {
    try {
      const params = { page: currentPage };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (sourceType) params.source_type = sourceType;
      const response = await getJournalEntries(token, params);
      setData({
        items: response.data || [],
        total: response.meta?.total || 0,
        serverLimit: response.meta?.limit || 10,
      });
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchEntries(); }, [currentPage, dateFrom, dateTo, sourceType, languageCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateParams = (newParams) => {
    const current = Object.fromEntries(searchParams.entries());
    setSearchParams({ ...current, ...newParams });
  };

  const totalPages = Math.ceil(data.total / data.serverLimit) || 1;

  const handleVoidClick = (id) => setModalConfig({ isOpen: true, id });

  const confirmVoid = async () => {
    try {
      setStatus("loading");
      await voidJournalEntry(token, modalConfig.id, "");
      setStatus("success");
      setTimeout(() => {
        setModalConfig({ isOpen: false, id: null });
        setStatus("idle");
      }, 1200);
      notify.success(t("notify_voided", "Journal entry voided — a reversing entry has been posted"));
      fetchEntries();
    } catch (err) {
      setStatus("error");
      notify.error(err.data?.error || err.message || t("notify_void_failed", "Failed to void journal entry"));
    }
  };

  const columns = [
    { key: "entry_number", label: t("col_entry_number", "Entry #"), className: "text-gray-700 font-mono font-bold" },
    { key: "entry_date", label: t("col_date", "Date"), className: "text-gray-500", render: (row) => new Date(row.entry_date).toLocaleDateString() },
    { key: "description", label: t("col_description", "Description"), className: "text-gray-700" },
    {
      key: "source_type",
      label: t("col_source", "Source"),
      render: (row) => (
        <span className="px-2 py-1 rounded-full text-[10px] font-bold uppercase bg-gray-100 text-gray-500">
          {t(`source_type_${row.source_type}`, row.source_type)}
        </span>
      ),
    },
    {
      key: "amount",
      label: t("col_amount", "Amount"),
      className: "text-gray-800 font-mono font-bold",
      render: (row) => money((row.lines || []).reduce((sum, l) => sum + (l.debit || 0), 0)),
    },
    {
      key: "status",
      label: t("col_status", "Status"),
      render: (row) => (
        <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase ${row.status === "voided" ? "bg-red-50 text-red-500" : "bg-green-50 text-green-600"}`}>
          {t(`entry_status_${row.status}`, row.status)}
        </span>
      ),
    },
  ];

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto transition-all duration-300">
      <ConfirmModal
        isOpen={modalConfig.isOpen}
        title={t("void_confirm_title", "Void Journal Entry")}
        message={t("void_confirm_message", "This posts a reversing entry — the original stays in the ledger, marked voided, for a full audit trail. Continue?")}
        onConfirm={confirmVoid}
        onCancel={() => setModalConfig({ isOpen: false, id: null })}
        isLoading={status === "loading"}
        isSuccess={status === "success"}
        isError={status === "error"}
        confirmLabel={t("btn_void", "Void")}
        successTitle={t("void_success_title", "Voided!")}
        successMessage={t("notify_voided", "Journal entry voided — a reversing entry has been posted")}
      />

      <PageHeader className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-6 border-b border-gray-100 pb-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/dashboard")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
            <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
          </button>
          <div className="flex items-center justify-center min-w-[48px] h-[48px] text-[var(--primary-color)]">
            <ICONS.JournalEntries size={40} className="shrink-0" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold" style={{ color: "var(--primary-color)" }}>{t("journal_page_title", "Journal Entries")}</h2>
            <p className="text-gray-400 text-xs md:text-sm font-medium">{t("journal_page_subtitle", "Every entry posted to the ledger — manual and automatic alike")}</p>
          </div>
        </div>

        <Button variant="primary" icon={ICONS.Plus} onClick={() => navigate("/add-journal-entry")} className="w-full sm:w-auto shrink-0">
          {t("btn_add_journal_entry", "New Journal Entry")}
        </Button>
      </PageHeader>

      <div className="mb-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <FloatingInput label={t("filter_date_from_label", "From")} type="date" value={dateFrom} onChange={(e) => updateParams({ date_from: e.target.value, page: 1 })} />
        <FloatingInput label={t("filter_date_to_label", "To")} type="date" value={dateTo} onChange={(e) => updateParams({ date_to: e.target.value, page: 1 })} />
        <SelectBox label={t("filter_source_label", "Source")} options={SOURCE_OPTIONS} value={sourceType} onChange={(val) => updateParams({ source_type: val, page: 1 })} />
      </div>

      <DataTable
        columns={columns}
        data={data.items}
        sortConfig={{ key: "entry_date", direction: "desc" }}
        onSort={() => {}}
        renderActions={(row, isMobile) => (
          <>
            <Button variant="outline" size="sm" icon={ICONS.JournalEntries} onClick={() => setDetailEntry(row)}>{isMobile && ` ${t("btn_view", "View")}`}</Button>
            {row.status !== "voided" && (
              <Button variant="danger" size="sm" icon={ICONS.Close} onClick={() => handleVoidClick(row.id)}>{isMobile && ` ${t("btn_void", "Void")}`}</Button>
            )}
          </>
        )}
        mobileRender={(row) => (
          <div className="flex justify-between items-center">
            <span className="text-[10px] text-gray-400 font-mono">{row.entry_number}</span>
            <h3 className="font-bold text-gray-800">{row.description}</h3>
          </div>
        )}
        paginationConfig={{
          currentPage,
          totalPages,
          onPageChange: (p) => updateParams({ page: p }),
          totalItems: data.total,
        }}
      />

      {detailEntry && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={() => setDetailEntry(null)}>
          <div className="bg-white rounded-[2rem] p-6 md:p-8 max-w-2xl w-full shadow-2xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-bold text-gray-900">{detailEntry.entry_number}</h3>
                <p className="text-gray-400 text-sm">{detailEntry.description}</p>
              </div>
              <button onClick={() => setDetailEntry(null)} className="p-2 hover:bg-gray-100 rounded-xl text-gray-400">
                <ICONS.Close className="w-6 h-6" />
              </button>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-start text-xs uppercase text-gray-400 border-b border-gray-100">
                  <th className="p-2 text-start">{t("col_account", "Account")}</th>
                  <th className="p-2 text-end">{t("col_debit", "Debit")}</th>
                  <th className="p-2 text-end">{t("col_credit", "Credit")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {(detailEntry.lines || []).map((l) => (
                  <tr key={l.id}>
                    <td className="p-2 font-medium text-gray-700">{l.account?.code} — {l.account?.name}</td>
                    <td className="p-2 text-end font-mono">{l.debit ? money(l.debit) : ""}</td>
                    <td className="p-2 text-end font-mono">{l.credit ? money(l.credit) : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
